import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '../../context/AuthContext';
import HelpTooltip from '../../components/HelpTooltip';

const upgradePromptStyle = { background: 'rgba(247,124,0,0.1)', border: '1px solid rgba(247,124,0,0.3)', borderRadius: 8, padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-muted)' };

export default function TeamCommunication() {
  const { apiFetch } = useAuth();
  const [teamInfo, setTeamInfo] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [isPaid, setIsPaid] = useState(true);

  // Push notification state
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushSchedule, setPushSchedule] = useState(false);
  const [pushDate, setPushDate] = useState('');
  const [pushTime, setPushTime] = useState('09:00');

  // Team email state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSchedule, setEmailSchedule] = useState(false);
  const [emailDate, setEmailDate] = useState('');
  const [emailTime, setEmailTime] = useState('09:00');
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);

  // Scheduled communications
  const [scheduled, setScheduled] = useState({ pushes: [], emails: [] });

  // Edit scheduled state
  const [editPush, setEditPush] = useState(null);
  const [editPushTitle, setEditPushTitle] = useState('');
  const [editPushBody, setEditPushBody] = useState('');
  const [editPushDate, setEditPushDate] = useState('');
  const [editPushTime, setEditPushTime] = useState('09:00');
  const [editPushSaving, setEditPushSaving] = useState(false);

  const [editEmail, setEditEmail] = useState(null);
  const [editEmailSubject, setEditEmailSubject] = useState('');
  const [editEmailBody, setEditEmailBody] = useState('');
  const [editEmailDate, setEditEmailDate] = useState('');
  const [editEmailTime, setEditEmailTime] = useState('09:00');
  const [editEmailSaving, setEditEmailSaving] = useState(false);

  const loadTeamInfo = useCallback(async () => {
    try {
      const data = await apiFetch('/api/admin/teams/current');
      setTeamInfo(data.team);
    } catch (err) { console.error(err); }
  }, [apiFetch]);

  const loadScheduled = useCallback(async () => {
    try {
      const data = await apiFetch('/api/admin/scheduled');
      setScheduled(data);
    } catch (err) { console.error(err); }
  }, [apiFetch]);

  useEffect(() => {
    loadTeamInfo();
    loadScheduled();
    apiFetch('/api/admin/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {});
  }, [loadTeamInfo, loadScheduled, apiFetch]);

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 5000);
  };

  // Daily email toggle
  const handleDailyEmailToggle = async () => {
    const newVal = !teamInfo?.daily_email_enabled;
    try {
      await apiFetch(`/api/admin/teams/${teamInfo.id}/daily-email`, {
        method: 'PUT',
        body: JSON.stringify({ daily_email_enabled: newVal }),
      });
      showMsg(newVal ? 'Daily lesson emails enabled.' : 'Daily lesson emails disabled.');
      loadTeamInfo();
    } catch (err) { alert(err.message); }
  };

  // Send push notification
  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    if (pushSchedule && !pushDate) return;
    setPushSending(true);
    try {
      const payload = { title: pushTitle.trim(), body: pushBody.trim() };
      if (pushSchedule && pushDate) {
        payload.send_at = new Date(`${pushDate}T${pushTime}:00`).toISOString();
      }
      const data = await apiFetch('/api/admin/push/send', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      showMsg(data.message || 'Push notification sent.');
      setShowPushModal(false);
      setPushTitle('');
      setPushBody('');
      setPushSchedule(false);
      setPushDate('');
      setPushTime('09:00');
      loadScheduled();
    } catch (err) { alert(err.message); }
    finally { setPushSending(false); }
  };

  // Send team email
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) return;
    if (emailSchedule && !emailDate) return;
    setEmailSending(true);
    try {
      const payload = { subject: emailSubject.trim(), html_body: emailBody };
      if (emailSchedule && emailDate) {
        payload.send_at = new Date(`${emailDate}T${emailTime}:00`).toISOString();
      }
      const data = await apiFetch('/api/admin/email/send', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      showMsg(data.message || 'Email sent.');
      setEmailSubject('');
      setEmailBody('');
      setEmailSchedule(false);
      setEmailDate('');
      setEmailTime('09:00');
      loadScheduled();
    } catch (err) { alert(err.message); }
    finally { setEmailSending(false); }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!testEmailAddr.trim() || !emailSubject.trim() || !emailBody.trim()) return;
    setTestEmailSending(true);
    try {
      const data = await apiFetch('/api/admin/email/test', {
        method: 'POST',
        body: JSON.stringify({ to: testEmailAddr.trim(), subject: emailSubject.trim(), html_body: emailBody }),
      });
      showMsg(data.message || 'Test email sent.');
      setShowTestEmail(false);
      setTestEmailAddr('');
    } catch (err) { alert(err.message); }
    finally { setTestEmailSending(false); }
  };

  // Delete scheduled
  const handleDeletePush = async (id) => {
    if (!window.confirm('Delete this scheduled push notification?')) return;
    try {
      await apiFetch(`/api/admin/scheduled/push/${id}`, { method: 'DELETE' });
      showMsg('Scheduled push deleted.');
      loadScheduled();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteEmail = async (id) => {
    if (!window.confirm('Delete this scheduled email?')) return;
    try {
      await apiFetch(`/api/admin/scheduled/email/${id}`, { method: 'DELETE' });
      showMsg('Scheduled email deleted.');
      loadScheduled();
    } catch (err) { alert(err.message); }
  };

  // Edit scheduled
  const openEditPush = (p) => {
    setEditPush(p);
    setEditPushTitle(p.title);
    setEditPushBody(p.body);
    const d = new Date(p.send_at);
    setEditPushDate(d.toISOString().split('T')[0]);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, '0');
    setEditPushTime(`${h}:${m}`);
  };

  const handleEditPushSave = async () => {
    if (!editPushTitle.trim() || !editPushBody.trim() || !editPushDate) return;
    setEditPushSaving(true);
    try {
      await apiFetch(`/api/admin/scheduled/push/${editPush.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: editPushTitle.trim(),
          body: editPushBody.trim(),
          send_at: new Date(`${editPushDate}T${editPushTime}:00`).toISOString(),
        }),
      });
      showMsg('Scheduled push updated.');
      setEditPush(null);
      loadScheduled();
    } catch (err) { alert(err.message); }
    finally { setEditPushSaving(false); }
  };

  const openEditEmail = (e) => {
    setEditEmail(e);
    setEditEmailSubject(e.subject);
    setEditEmailBody(e.html_body);
    const d = new Date(e.send_at);
    setEditEmailDate(d.toISOString().split('T')[0]);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, '0');
    setEditEmailTime(`${h}:${m}`);
  };

  const handleEditEmailSave = async () => {
    if (!editEmailSubject.trim() || !editEmailBody.trim() || !editEmailDate) return;
    setEditEmailSaving(true);
    try {
      await apiFetch(`/api/admin/scheduled/email/${editEmail.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          subject: editEmailSubject.trim(),
          html_body: editEmailBody,
          send_at: new Date(`${editEmailDate}T${editEmailTime}:00`).toISOString(),
        }),
      });
      showMsg('Scheduled email updated.');
      setEditEmail(null);
      loadScheduled();
    } catch (err) { alert(err.message); }
    finally { setEditEmailSaving(false); }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9em' };
  const labelStyle = { fontSize: '0.85em', fontWeight: 600, display: 'block', marginBottom: 4 };

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Team Communication</h1>
        <HelpTooltip>
          <h3>Team Communication</h3>
          <p><strong>Daily Lesson Emails</strong> — When enabled, players automatically receive an email at 8 AM ET on days you have a drill scheduled. Toggle this on or off anytime.</p>
          <p><strong>Push Notifications</strong> — Send an instant push notification to every player on the team who has notifications enabled. Good for reminders or announcements.</p>
          <p><strong>Team Email</strong> — Compose and send a custom email to all players and parents on your team. You can format text, add links, and include images.</p>
        </HelpTooltip>
      </div>

      {actionMsg && (
        <div style={{ background: '#e8f5e9', border: '1px solid #c8e6c9', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.9em', textAlign: 'center' }}>
          {actionMsg}
        </div>
      )}

      {/* Daily Lesson Emails */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Daily Lesson Emails</div>
        <span style={{ fontSize: '0.8em', color: '#666' }}>
          Players automatically receive a daily email at 8 AM ET on days you have a drill scheduled.
        </span>
      </div>

      {/* Send Push Notification */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Push Notifications</div>
        <p style={{ fontSize: '0.8em', color: '#666', marginBottom: 10, marginTop: 0 }}>
          Send an instant push notification to every player on the team who has notifications enabled.
        </p>
        {teamInfo?.is_demo ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>This is not available on demo accounts.</div>
        ) : !isPaid ? (
          <div style={upgradePromptStyle}>
            <i className="fas fa-lock" style={{ marginRight: 8 }}></i>
            Push notifications are available on paid plans. <a href="/admin/upgrade?from=push" style={{ color: 'var(--orange)', fontWeight: 600 }}>Upgrade your plan →</a>
          </div>
        ) : (
          <button className="btn btn-outline btn-sm" onClick={() => setShowPushModal(true)}>Send Push Notification</button>
        )}
      </div>

      {/* Send Team Email */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Send Team Email</div>
        <p style={{ fontSize: '0.8em', color: '#666', marginBottom: 10, marginTop: 0 }}>
          Compose and send a custom email to all players and parents on your team.
        </p>
        {teamInfo?.is_demo ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>This is not available on demo accounts.</div>
        ) : !isPaid ? (
          <div style={upgradePromptStyle}>
            <i className="fas fa-lock" style={{ marginRight: 8 }}></i>
            Custom team emails are available on paid plans. <a href="/admin/upgrade?from=email" style={{ color: 'var(--orange)', fontWeight: 600 }}>Upgrade your plan →</a>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Subject</label>
              <input
                type="text"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                placeholder="Email subject line"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message</label>
              <ReactQuill
                theme="snow"
                value={emailBody}
                onChange={setEmailBody}
                modules={quillModules}
                placeholder="Compose your email..."
                style={{ background: '#fff', borderRadius: 6 }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85em' }}>
                <input
                  type="checkbox"
                  checked={emailSchedule}
                  onChange={e => setEmailSchedule(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontWeight: 600 }}>Schedule Email?</span>
              </label>
              {emailSchedule && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <input
                    type="date"
                    value={emailDate}
                    onChange={e => setEmailDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{ flex: 1, ...inputStyle }}
                  />
                  <select
                    value={emailTime}
                    onChange={e => setEmailTime(e.target.value)}
                    style={inputStyle}
                  >
                    {Array.from({ length: 24 * 4 }, (_, i) => {
                      const h = Math.floor(i / 4);
                      const m = (i % 4) * 15;
                      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-orange btn-sm"
                onClick={handleSendEmail}
                disabled={emailSending || !emailSubject.trim() || !emailBody.trim() || (emailSchedule && !emailDate)}
              >
                {emailSending ? 'Sending...' : emailSchedule ? 'Schedule Email' : 'Send Email'}
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowTestEmail(!showTestEmail)}
              >
                Send Test Email
              </button>
            </div>
            {showTestEmail && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="email"
                  value={testEmailAddr}
                  onChange={e => setTestEmailAddr(e.target.value)}
                  placeholder="test@example.com"
                  style={{ flex: 1, ...inputStyle }}
                />
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleSendTestEmail}
                  disabled={testEmailSending || !testEmailAddr.trim() || !emailSubject.trim() || !emailBody.trim()}
                >
                  {testEmailSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Scheduled Communications */}
      {(scheduled.pushes.length > 0 || scheduled.emails.length > 0) && (
        <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>Scheduled Communications</div>
          {scheduled.pushes.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <div>
                <span style={{ fontSize: '0.7em', fontWeight: 700, background: '#e3f2fd', color: '#1565c0', padding: '2px 6px', borderRadius: 4, marginRight: 8 }}>PUSH</span>
                <span style={{ fontSize: '0.85em', fontWeight: 600 }}>{p.title}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75em', color: '#999' }}>
                  {new Date(p.send_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
                <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: '0.75em' }} onClick={() => openEditPush(p)}>Edit</button>
                <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: '0.75em' }} onClick={() => handleDeletePush(p.id)}>Delete</button>
              </div>
            </div>
          ))}
          {scheduled.emails.map(e => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <div>
                <span style={{ fontSize: '0.7em', fontWeight: 700, background: '#fce4ec', color: '#c62828', padding: '2px 6px', borderRadius: 4, marginRight: 8 }}>EMAIL</span>
                <span style={{ fontSize: '0.85em', fontWeight: 600 }}>{e.subject}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75em', color: '#999' }}>
                  {new Date(e.send_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
                <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: '0.75em' }} onClick={() => openEditEmail(e)}>Edit</button>
                <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: '0.75em' }} onClick={() => handleDeleteEmail(e.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Push Notification Modal */}
      {showPushModal && (
        <div className="modal-overlay" onClick={() => setShowPushModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 style={{ marginBottom: 16 }}>Send Push Notification</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                value={pushTitle}
                onChange={e => setPushTitle(e.target.value)}
                placeholder="Notification title"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Message</label>
              <textarea
                value={pushBody}
                onChange={e => setPushBody(e.target.value)}
                placeholder="Notification message"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85em' }}>
                <input
                  type="checkbox"
                  checked={pushSchedule}
                  onChange={e => setPushSchedule(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontWeight: 600 }}>Schedule Notification?</span>
              </label>
              {pushSchedule && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <input
                    type="date"
                    value={pushDate}
                    onChange={e => setPushDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{ flex: 1, ...inputStyle }}
                  />
                  <select
                    value={pushTime}
                    onChange={e => setPushTime(e.target.value)}
                    style={inputStyle}
                  >
                    {Array.from({ length: 24 * 4 }, (_, i) => {
                      const h = Math.floor(i / 4);
                      const m = (i % 4) * 15;
                      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowPushModal(false)}>Cancel</button>
              <button
                className="btn btn-orange btn-sm"
                onClick={handleSendPush}
                disabled={pushSending || !pushTitle.trim() || !pushBody.trim() || (pushSchedule && !pushDate)}
              >
                {pushSending ? 'Sending...' : pushSchedule ? 'Schedule' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Scheduled Push Modal */}
      {editPush && (
        <div className="modal-overlay" onClick={() => setEditPush(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 style={{ marginBottom: 16 }}>Edit Scheduled Push</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                value={editPushTitle}
                onChange={e => setEditPushTitle(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message</label>
              <textarea
                value={editPushBody}
                onChange={e => setEditPushBody(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Send At</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="date"
                  value={editPushDate}
                  onChange={e => setEditPushDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ flex: 1, ...inputStyle }}
                />
                <select
                  value={editPushTime}
                  onChange={e => setEditPushTime(e.target.value)}
                  style={inputStyle}
                >
                  {Array.from({ length: 24 * 4 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                    const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setEditPush(null)}>Cancel</button>
              <button
                className="btn btn-orange btn-sm"
                onClick={handleEditPushSave}
                disabled={editPushSaving || !editPushTitle.trim() || !editPushBody.trim() || !editPushDate}
              >
                {editPushSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Scheduled Email Modal */}
      {editEmail && (
        <div className="modal-overlay" onClick={() => setEditEmail(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3 style={{ marginBottom: 16 }}>Edit Scheduled Email</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Subject</label>
              <input
                type="text"
                value={editEmailSubject}
                onChange={e => setEditEmailSubject(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message</label>
              <ReactQuill
                theme="snow"
                value={editEmailBody}
                onChange={setEditEmailBody}
                modules={quillModules}
                style={{ background: '#fff', borderRadius: 6 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Send At</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="date"
                  value={editEmailDate}
                  onChange={e => setEditEmailDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ flex: 1, ...inputStyle }}
                />
                <select
                  value={editEmailTime}
                  onChange={e => setEditEmailTime(e.target.value)}
                  style={inputStyle}
                >
                  {Array.from({ length: 24 * 4 }, (_, i) => {
                    const h = Math.floor(i / 4);
                    const m = (i % 4) * 15;
                    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                    const label = new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setEditEmail(null)}>Cancel</button>
              <button
                className="btn btn-orange btn-sm"
                onClick={handleEditEmailSave}
                disabled={editEmailSaving || !editEmailSubject.trim() || !editEmailBody.trim() || !editEmailDate}
              >
                {editEmailSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
